import { TableTitlesContext } from '../components/table-titles-context'
import { useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { postQueryTable } from '../fetch/post-query-table'
import { postEditTable } from '../fetch/post-edit-table'
import { FilterOperator, TableEdit, TablePage, TableQuery, type TableEditResult } from '../schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackTableEdited } from '@/analytics/utils/table-analytics'
import { ApiError } from '@/util/errors/api-error'

const noHiddenColumns: string[] = []

export enum TableErrorRecovery {
  Reload = 'reload',
  ClearView = 'clear-view',
  BackToTables = 'back-to-tables',
}

/** Owns one page's request lifecycle and reconciles every attempted mutation. */
export function useTablePage(
  tableId: string,
  query: TableQuery,
  refreshKey = '',
  hiddenColumns = noHiddenColumns,
  onQueryChange?: (query: TableQuery) => void
) {
  const titles = useContext(TableTitlesContext)
  const { trackEvent } = useAnalytics()
  const [result, setResult] = useState<{ page: TablePage; query: TableQuery; refreshVersion: string } | null>(null)
  const knownPage = useRef<TablePage | null>(null)
  // Recovery metadata can advance before the filtered rows are ready to display.
  const [metadata, setMetadata] = useState<TablePage | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Column IDs are UUIDs. A primitive dependency keeps equivalent response metadata from refetching.
  const columnIds = metadata?.table.id === tableId ? metadata.columns.map((column) => column.id).join(',') : ''
  const hiddenIds = hiddenColumns.join(',')
  const documentBased = metadata?.table.id === tableId && metadata.document_based
  const requestQuery = useMemo(
    () =>
      query.search?.trim()
        ? {
            ...query,
            search_column_ids: columnIds ? columnIds.split(',').filter((id) => !hiddenIds.split(',').includes(id)) : [],
            search_filenames: documentBased && !hiddenIds.split(',').includes('row'),
          }
        : query,
    [query, columnIds, documentBased, hiddenIds]
  )
  // A successful reconciliation read must not hide a failed mutation.
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<{ message: string; recovery: TableErrorRecovery } | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refresh, setRefresh] = useState(0)
  const mutationInFlight = useRef(false)
  const reload = useCallback(() => setRefresh((value) => value + 1), [])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    const reconcile = (page: TablePage) => {
      if (controller.signal.aborted) return false
      const previous = knownPage.current?.table.id === tableId ? knownPage.current : null
      const changed = page.columns.filter((column) => {
        const old = previous?.columns.find((item) => item.id === column.id)
        return old && old.kind !== column.kind
      })
      const deleted = previous?.columns.filter((column) => !page.columns.some((item) => item.id === column.id)) ?? []
      if ((!changed.length && !deleted.length) || !onQueryChange) return false
      const filters = requestQuery.filters.filter(
        (filter) =>
          !deleted.some((column) => column.id === filter.column_id) &&
          (!changed.some((column) => column.id === filter.column_id) ||
            filter.operator === FilterOperator.Contains ||
            filter.operator === FilterOperator.IsBlank ||
            filter.operator === FilterOperator.IsNotBlank)
      )
      const removed = requestQuery.filters.length - filters.length
      knownPage.current = page
      setMetadata(page)
      const sorts = requestQuery.sorts.filter((sort) => !deleted.some((column) => column.id === sort.column_id))
      const removedSorts = requestQuery.sorts.length - sorts.length
      const deletedColumnFilters = requestQuery.filters.filter((filter) => deleted.some((column) => column.id === filter.column_id)).length
      const changedTypeFilters = removed - deletedColumnFilters
      const notices: string[] = []
      if (deletedColumnFilters && removedSorts) {
        const affectedColumns = deleted.filter(
          (column) => requestQuery.filters.some((filter) => filter.column_id === column.id) || requestQuery.sorts.some((sort) => sort.column_id === column.id)
        ).length
        notices.push(
          `Removed ${deletedColumnFilters} filter${deletedColumnFilters === 1 ? '' : 's'} and ${removedSorts} sort${
            removedSorts === 1 ? '' : 's'
          } because their ${affectedColumns === 1 ? 'column was' : 'columns were'} deleted.`
        )
      } else if (deletedColumnFilters) {
        notices.push(
          `Removed ${deletedColumnFilters} filter${deletedColumnFilters === 1 ? '' : 's'} because ${
            deletedColumnFilters === 1 ? 'its column was' : 'their columns were'
          } deleted.`
        )
      }
      if (changedTypeFilters) {
        notices.push(
          `Removed ${changedTypeFilters} filter${changedTypeFilters === 1 ? '' : 's'} because ${
            changedTypeFilters === 1 ? 'its column type' : 'their column types'
          } changed.`
        )
      }
      if (removedSorts && !deletedColumnFilters) {
        notices.push(
          `Removed ${removedSorts} sort${removedSorts === 1 ? '' : 's'} because ${removedSorts === 1 ? 'its column was' : 'their columns were'} deleted.`
        )
      }
      setNotice(notices.join(' ') || null)
      onQueryChange({ ...query, filters, sorts, offset: 0 })
      return true
    }
    const load = async () => {
      let page: TablePage
      try {
        page = await postQueryTable(tableId, requestQuery, controller.signal)
      } catch (reason) {
        // Deleted columns and stale typed filters can fail before new metadata arrives.
        if (
          reason instanceof ApiError &&
          reason.status === 422 &&
          (requestQuery.filters.length || requestQuery.sorts.length || requestQuery.search?.trim()) &&
          knownPage.current?.table.id === tableId &&
          onQueryChange &&
          !controller.signal.aborted
        ) {
          const probePage = await postQueryTable(
            tableId,
            { ...requestQuery, filters: [], sorts: [], search: '', search_column_ids: [], search_filenames: false, offset: 0, limit: 1 },
            controller.signal
          )
          if (reconcile(probePage)) return
        }
        throw reason
      }
      if (controller.signal.aborted) return
      setLoadError(null)
      if (!reconcile(page)) {
        knownPage.current = page
        setMetadata(page)
        setResult({ page, query: requestQuery, refreshVersion: `${refreshKey}:${refresh}` })
      }
    }
    load()
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          if (reason instanceof ApiError && (reason.status === 403 || reason.status === 404)) setResult(null)
          setLoadError({
            message: reason instanceof Error ? reason.message : 'Unable to load this table.',
            recovery:
              reason instanceof ApiError && (reason.status === 403 || reason.status === 404)
                ? TableErrorRecovery.BackToTables
                : reason instanceof ApiError && reason.status === 422 && (requestQuery.filters.length > 0 || requestQuery.sorts.length > 0)
                ? TableErrorRecovery.ClearView
                : TableErrorRecovery.Reload,
          })
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [tableId, query, requestQuery, refresh, refreshKey, onQueryChange])

  const edit = async (request: TableEdit): Promise<TableEditResult> => {
    if (mutationInFlight.current) return { ok: false, error: 'Another change is being saved. Please try again.', conflict: false }
    mutationInFlight.current = true
    setSaving(true)
    setMutationError(null)
    try {
      await postEditTable(tableId, request)
      trackTableEdited(trackEvent, tableId, request)
      return { ok: true }
    } catch (reason) {
      const conflict = request.action === 'edit-cell' && reason instanceof ApiError && reason.status === 409
      const error = conflict
        ? 'This cell changed while you were editing. Your edit wasn’t saved. Please edit the updated value.'
        : reason instanceof Error
        ? reason.message
        : 'Unable to save the change.'
      if (request.action !== 'add-column' && request.action !== 'add-analysis-column') setMutationError(error)
      return { ok: false, error, conflict }
    } finally {
      mutationInFlight.current = false
      setSaving(false)
      if (request.action === 'rename-table') titles?.refresh()
      reload()
    }
  }
  return {
    data: result?.page ?? null,
    refreshVersion: result?.refreshVersion ?? `${refreshKey}:${refresh}`,
    requestQuery,
    displayedQuery: result?.query ?? requestQuery,
    error: mutationError ?? loadError?.message ?? null,
    errorRecovery: loadError?.recovery ?? null,
    notice,
    loading,
    saving,
    edit,
    reload,
    clearError: () => {
      setMutationError(null)
      setLoadError(null)
    },
    clearNotice: () => setNotice(null),
  }
}
